import {
  ChartIcon,
  Divider,
  FormControlLabel,
  GKButton,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  PercentageIcon,
  RadioGroup,
  Show,
  Typography,
} from "components";
import {
  AllocationByTooltipContent,
  AllocationTypeTooltipContent,
  ExitReferenceValueCompanyTooltip,
  ModelReferenceWeightage,
  ReferenceValueCompanyTooltip,
  ReferenceValueTooltip,
  WeightageDistributionMethod,
} from "Pages/EquityBasket/OrderSimulation/TooltipContent";
import { Controller, FormProvider } from "react-hook-form";
import { handleCapitalize } from "utils/common";
import useOrders, {
  getInitialOrderFormValues,
  OrderFormValues,
} from "./useOrders";

type OrderFormResetValues = Partial<Omit<OrderFormValues, "allocation">> & {
  allocation?: Partial<OrderFormValues["allocation"]>;
};

const FormInputs = () => {
  const {
    form,
    ALLOCATION_TYPE,
    watchAllocationType,
    ALLOCATION_BY_EXIT,
    ALLOCATION_BY,
    watchAllocationBy,
    TRANSACTION_TYPE,
    REFERENCE_TYPE,
    WEIGHTAGE_DISTRIBUTION_METHOD,
    companies,
    CUSTOMIZED_PARAMETER,
    watchRightVisibleTable,
  } = useOrders();

  const resetOrderForm = (values: OrderFormResetValues) => {
    const initialValues = getInitialOrderFormValues();

    form.reset({
      ...initialValues,
      ...values,
      allocation: {
        ...initialValues.allocation,
        ...values.allocation,
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form style={{ height: "100%", width: "100%" }}>
        <Grid2 container gap={3}>
          <Grid2 size={12}>
            <Controller
              name="basket"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select basket",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="Basket Name"
                  requiredField
                  options={[]}
                  getOptionLabel={(e) => e.name || e}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12}>
            <Typography>
              Transaction Type <span style={{ color: "red" }}> *</span>
            </Typography>
            <Controller
              control={form.control}
              name="transactionType"
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  row
                  onChange={(e) => {
                    const selectedTransactionType = e.target.value;

                    resetOrderForm({
                      basket: form.getValues("basket"),
                      transactionType: selectedTransactionType,
                    });
                  }}
                >
                  <FormControlLabel
                    label="Lumpsum"
                    value="lumpsum"
                    control={<GKRadio disableLeftPadding />}
                  />
                  <FormControlLabel
                    label="Plan (SIP)"
                    value="sip"
                    control={<GKRadio disableLeftPadding />}
                  />
                </RadioGroup>
              )}
            />
          </Grid2>
          <Show.When isTrue={form.watch("transactionType") === "lumpsum"}>
            <Grid2 size={12}>
              <Divider />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                control={form.control}
                name="allocation.allocType"
                render={({ field }) => (
                  <GKSearchSelect
                    {...field}
                    disableClearable
                    inputLabel="Allocation Type"
                    options={ALLOCATION_TYPE}
                    onChange={(event, val) => {
                      const selectedAllocType = val?.value || "";

                      resetOrderForm({
                        basket: form.getValues("basket"),
                        transactionType: form.getValues("transactionType"),
                        allocation: {
                          allocType: selectedAllocType,
                        },
                      });
                    }}
                    value={
                      ALLOCATION_TYPE.find(
                        (type) => type.value === field.value,
                      ) || null
                    }
                    getOptionLabel={(option) => option.name || option}
                    tooltipModal={{
                      isTrue: true,
                      tooltipModelLabel: "Allocation Type",
                      tooltipModalContent: <AllocationTypeTooltipContent />,
                    }}
                  />
                )}
              />
            </Grid2>
            <Show.When isTrue={!!watchAllocationType}>
              <Grid2 size={12} container spacing={4}>
                <Grid2
                  size={12}
                  container
                  bgcolor={" #F8F8F8"}
                  borderRadius={1}
                  p={"16px 12px"}
                >
                  <Grid2 size={12}>
                    <Controller
                      control={form.control}
                      name="allocation.allocationBy"
                      render={({ field }) => (
                        <GKSearchSelect
                          {...field}
                          disableClearable
                          inputLabel={
                            watchAllocationType === "allocation"
                              ? "Allocation By"
                              : watchAllocationType === "rebalance"
                                ? "Rebalance By"
                                : "Exit By"
                          }
                          options={
                            watchAllocationType === "exit"
                              ? ALLOCATION_BY_EXIT
                              : ALLOCATION_BY.filter((dataItem) =>
                                  watchAllocationType !== "rebalance"
                                    ? dataItem.value !== "customized"
                                    : dataItem,
                                )
                          }
                          onChange={(event, val) => {
                            const selectedAllocBy = val?.value || "";

                            resetOrderForm({
                              basket: form.getValues("basket"),
                              transactionType:
                                form.getValues("transactionType"),
                              allocation: {
                                allocType: form.getValues(
                                  "allocation.allocType",
                                ),
                                allocationBy: selectedAllocBy,
                              },
                            });
                          }}
                          value={
                            (watchAllocationType === "exit"
                              ? ALLOCATION_BY_EXIT
                              : ALLOCATION_BY
                            ).find((type) => type.value === field.value) || null
                          }
                          getOptionLabel={(option) => option.name || option}
                          tooltipModal={{
                            isTrue: true,
                            tooltipModelLabel:
                              watchAllocationType === "allocation"
                                ? "Allocation By"
                                : watchAllocationType === "rebalance"
                                  ? "Rebalance By"
                                  : "Exit By",
                            tooltipModalContent: (
                              <AllocationByTooltipContent
                                allocationType={watchAllocationType}
                              />
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid2>
                  <Show.When
                    isTrue={
                      (watchAllocationType === "allocation" &&
                        watchAllocationBy === "version") ||
                      (watchAllocationType === "rebalance" &&
                        watchAllocationBy !== "customized")
                    }
                  >
                    <Grid2 size={12}>
                      <Controller
                        control={form.control}
                        name="allocation.useCurrentAllocation"
                        render={({ field }) => {
                          return (
                            <GKSearchSelect
                              {...field}
                              disableClearable
                              inputLabel="Model Reference Weightage"
                              options={TRANSACTION_TYPE}
                              onChange={(event, val) => {
                                field.onChange(val?.value);
                              }}
                              value={
                                TRANSACTION_TYPE?.find(
                                  (item) => item.value === field.value,
                                ) || null
                              }
                              getOptionLabel={(option) => option.name || option}
                              tooltipModal={{
                                isTrue: true,
                                tooltipModelLabel: "Model Reference Weightage",
                                tooltipModalContent: (
                                  <ModelReferenceWeightage />
                                ),
                              }}
                            />
                          );
                        }}
                      />
                    </Grid2>
                  </Show.When>
                  <Show.When
                    isTrue={
                      watchAllocationType === "allocation" ||
                      (watchAllocationType === "exit" &&
                        watchAllocationBy === "companyWeightage") ||
                      watchAllocationType === "rebalance"
                    }
                  >
                    <Grid2 size={12}>
                      <Controller
                        control={form.control}
                        name="allocation.rebalanceUsing"
                        render={({ field }) => (
                          <GKSearchSelect
                            {...field}
                            disableClearable
                            inputLabel={`${handleCapitalize(watchAllocationType)} Reference Value`}
                            options={REFERENCE_TYPE.filter(
                              (dataItem) =>
                                dataItem?.allocationType?.includes(
                                  watchAllocationType,
                                ) &&
                                dataItem[
                                  watchAllocationType as keyof typeof dataItem
                                ]?.includes(watchAllocationBy),
                            ).map((dataItem) => ({
                              ...dataItem,
                              label: dataItem.name,
                              value: dataItem.value,
                            }))}
                            onChange={(event, val) =>
                              field.onChange(val?.value)
                            }
                            value={
                              REFERENCE_TYPE.filter(
                                (dataItem) =>
                                  dataItem?.allocationType?.includes(
                                    watchAllocationType,
                                  ) &&
                                  dataItem[
                                    watchAllocationType as keyof typeof dataItem
                                  ]?.includes(watchAllocationBy),
                              )
                                .map((dataItem) => ({
                                  ...dataItem,
                                  label: dataItem.name,
                                  value: dataItem.value,
                                }))
                                .find(
                                  (option) => option.value === field.value,
                                ) || null
                            }
                            getOptionLabel={(option) =>
                              typeof option === "string" ? option : option.label
                            }
                            tooltipModal={{
                              isTrue: true,
                              tooltipModelLabel: `${handleCapitalize(watchAllocationType)} Reference Value`,
                              tooltipModalContent:
                                watchAllocationType === "allocation" &&
                                watchAllocationBy === "company" ? (
                                  <ReferenceValueCompanyTooltip />
                                ) : watchAllocationType === "exit" &&
                                  watchAllocationBy === "companyWeightage" ? (
                                  <ExitReferenceValueCompanyTooltip />
                                ) : (
                                  <ReferenceValueTooltip
                                    allocationType={watchAllocationType}
                                  />
                                ),
                            }}
                          />
                        )}
                      />
                    </Grid2>
                  </Show.When>
                  <Show.When
                    isTrue={
                      (watchAllocationType === "allocation" &&
                        watchAllocationBy === "version") ||
                      (watchAllocationType === "rebalance" &&
                        watchAllocationBy !== "customized")
                    }
                  >
                    <Grid2 size={12}>
                      <Controller
                        control={form.control}
                        name="allocation.weightageDistributionMethod"
                        render={({ field }) => (
                          <GKSearchSelect
                            {...field}
                            disableClearable
                            inputLabel="Weightage Distribution Method"
                            options={WEIGHTAGE_DISTRIBUTION_METHOD}
                            onChange={(event, val) => {
                              field.onChange(val?.value);
                            }}
                            value={
                              WEIGHTAGE_DISTRIBUTION_METHOD.find(
                                (type) => type.value === field.value,
                              ) || null
                            }
                            getOptionLabel={(option) => option.name || option}
                            tooltipModal={{
                              isTrue: true,
                              tooltipModelLabel:
                                "Weightage Distribution Method",
                              tooltipModalContent: (
                                <WeightageDistributionMethod />
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid2>
                  </Show.When>
                  <Show.When
                    isTrue={
                      watchAllocationType === "rebalance" &&
                      watchAllocationBy === "company"
                    }
                  >
                    <Grid2 size={12} mr={4}>
                      <Controller
                        control={form.control}
                        name="allocation.companies"
                        render={({ field }) => (
                          <GKSearchSelect
                            {...field}
                            disableClearable={false}
                            inputLabel="Company Name"
                            options={companies || []}
                            value={
                              companies?.find((option: any) =>
                                (field.value || [])
                                  .map((dataItem: any) => dataItem?.value)
                                  .includes(option?.value),
                              ) || null
                            }
                            onChange={(e, val) => {
                              field.onChange(
                                val ? [{ ...val, weightage: 0 }] : [],
                              );
                            }}
                            getOptionLabel={(option) => option.name || option}
                          />
                        )}
                      />
                    </Grid2>
                  </Show.When>
                  {/* <Show.When
                  isTrue={
                    (["allocation", "exit"].includes(watchAllocationType) &&
                      ["version"].includes(watchAllocationBy)) ||
                    (watchAllocationType === "rebalance" &&
                      watchAllocationBy !== "customized")
                  }
                >
                  <Grid2 size={12} mr={4}>
                    <Controller
                      control={form.control}
                      name="allocation.allocVersion"
                      render={({ field }) => {
                        const selectedOption =
                          allocationVersionList?.data?.data?.find(
                            (dataItem: { id: number }) =>
                              dataItem.id === field.value,
                          ) || null;

                        return (
                          <GKSearchSelect
                            loading={allocationVersionListLoading}
                            inputLabel="Select Model"
                            value={selectedOption}
                            onChange={(e, val) => {
                              field.onChange(val ? val?.id : null);
                            }}
                            disableClearable={false}
                            options={allocationVersionList?.data?.data || []}
                            getOptionLabel={(option) =>
                              option?.version || option
                            }
                          />
                        );
                      }}
                    />
                  </Grid2>
                </Show.When> */}
                  <Show.When
                    isTrue={
                      ["exit"].includes(watchAllocationType) &&
                      ["version"].includes(watchAllocationBy)
                    }
                  >
                    <Grid2 size={12} mr={4}>
                      <Controller
                        control={form.control}
                        name="allocation.exitWeightage"
                        render={({ field }) => (
                          <GKTextField
                            {...field}
                            size="medium"
                            placeholder="Weightage"
                            type="number"
                            onChange={(e) => {
                              const val = e.target.value;
                              field.onChange(val === "" ? "" : Number(val));
                            }}
                            slotProps={{
                              input: { endAdornment: <PercentageIcon /> },
                            }}
                          />
                        )}
                      />
                    </Grid2>
                  </Show.When>
                  <Show.When
                    isTrue={
                      watchAllocationType === "rebalance" &&
                      watchAllocationBy === "customized"
                    }
                  >
                    <Grid2 size={12} mr={4}>
                      <Controller
                        control={form.control}
                        name="allocation.exitParameter"
                        render={({ field }) => {
                          const selectedOption =
                            CUSTOMIZED_PARAMETER.find(
                              (dataItem: { value: string }) =>
                                dataItem.value === field.value,
                            ) || null;

                          return (
                            <GKSearchSelect
                              // loading={allocationVersionListLoading}
                              inputLabel="Exit Parameter"
                              value={selectedOption}
                              onChange={(e, val) => {
                                field.onChange(val ? val?.value : null);
                              }}
                              disableClearable={false}
                              options={
                                CUSTOMIZED_PARAMETER.filter(
                                  (dataItem) =>
                                    dataItem?.value !== "sellAmountAllocation",
                                ) || []
                              }
                              getOptionLabel={(option) =>
                                option?.version || option
                              }
                            />
                          );
                        }}
                      />
                    </Grid2>
                  </Show.When>

                  <Show.When
                    isTrue={
                      watchAllocationType === "rebalance" &&
                      watchAllocationBy === "customized"
                    }
                  >
                    <Grid2 size={12} mr={4}>
                      <Controller
                        control={form.control}
                        name="allocation.buyParameter"
                        render={({ field }) => {
                          const selectedOption =
                            CUSTOMIZED_PARAMETER.find(
                              (dataItem: { value: string }) =>
                                dataItem.value === field.value,
                            ) || null;

                          return (
                            <GKSearchSelect
                              // loading={allocationVersionListLoading}
                              inputLabel="Buy Parameter"
                              value={selectedOption}
                              onChange={(e, val) => {
                                field.onChange(val ? val?.value : null);
                              }}
                              disableClearable={false}
                              options={
                                CUSTOMIZED_PARAMETER.filter(
                                  (dataItem) =>
                                    dataItem?.value !== "companyAbsolute",
                                ) || []
                              }
                              getOptionLabel={(option) =>
                                option?.version || option
                              }
                            />
                          );
                        }}
                      />
                    </Grid2>
                  </Show.When>
                </Grid2>
                <Show.When
                  isTrue={
                    ![
                      "EDIT_PORTFOLIO",
                      "EDIT_COMPANY",
                      "GENERATE_TRADE_TABLE",
                    ].includes(watchRightVisibleTable)
                  }
                >
                  <Grid2 size={12}>
                    <Controller
                      control={form.control}
                      name="extraThings.rightVisibleTable"
                      render={({ field }) => (
                        <GKButton
                          fullWidth
                          variant="contained"
                          startIcon={<ChartIcon />}
                          onClick={() => field.onChange("SELECT_PORTFOLIO")}
                        >
                          Select Portfolio
                        </GKButton>
                      )}
                    />
                  </Grid2>
                </Show.When>
              </Grid2>
            </Show.When>
          </Show.When>
        </Grid2>
      </form>
    </FormProvider>
  );
};

export default FormInputs;
